import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { ArrowLeft, ThumbsUp, Truck, MapPin, Clock, Shield, CheckCircle } from 'lucide-react';
import DownloadApp from '@/components/shared/DownloadApp';
import { getFullUrl } from "@/lib/utils";

const Delivery = () => {
  const deliveryOptions = [
    {
      title: "Express Delivery",
      description: "Same-day delivery within Johannesburg",
      time: "2-4 hours",
      price: "R150",
      features: ["GPS tracking", "SMS notifications", "Secure packaging"]
    },
    {
      title: "Standard Delivery",
      description: "Next business day delivery",
      time: "1 business day",
      price: "R75",
      features: ["Signature required", "Insurance included", "Delivery confirmation"]
    },
    {
      title: "Collection Service",
      description: "Collect from our offices",
      time: "Available now",
      price: "Free",
      features: ["Extended hours", "Secure storage", "ID verification required"]
    }
  ];

  const completionSteps = [
    {
      title: "Quality Verification",
      description: "All documents checked for accuracy and completeness",
      completed: true
    },
    {
      title: "Secure Packaging",
      description: "Documents placed in tamper-evident envelope",
      completed: true
    },   
    {
      title: "Collection or Delivery Preparation",
      description: "Ready for delivery or collection",
      completed: true
    },
    {
      title: "Customer Confirmation",
      description: "Final confirmation and receipt",
      completed: false
      }
    
  ];

  return (
    <>
      <Head>
        <title>Delivery & Completion - We Queue For You | EzyFind Car Spares</title>
        <meta name="description" content="Secure delivery options for your completed vehicle licensing documents. Express and standard delivery available." />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-blue-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link href={getFullUrl("we-queue-for-you")} className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-4">
              <ArrowLeft className="w-5 h-5" />
              Back to We Queue For You
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold">Delivery & Completion</h1>
            <p className="text-xl text-blue-100 mt-2">Secure delivery of your completed documents</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Completion Status */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <ThumbsUp className="w-8 h-8 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">Service Completed</h2>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800 font-medium">✅ Your vehicle licensing has been successfully processed!</p>
              </div>

              <div className="space-y-4 mb-6">
                {completionSteps.map((step, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      step.completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {step.completed ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <span className="text-xs">{index + 1}</span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{step.title}</h3>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-gray-900 mb-3">Documents Ready:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Vehicle licence disc (valid until 31/12/2025)</li>
                  <li>• Updated registration certificate</li>
                  <li>• Official receipts and confirmations</li>
                </ul>
              </div>
            </div>

            {/* Delivery Options */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Delivery Option</h2>
                
                <div className="space-y-4">
                  {deliveryOptions.map((option, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-900">{option.title}</h3>
                        <span className="text-lg font-bold text-blue-600">{option.price}</span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{option.description}</p>
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">{option.time}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {option.features.map((feature, featureIndex) => (
                          <span key={featureIndex} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                  <h3 className="text-xl font-bold text-gray-900">Security & Insurance</h3>
                </div>
                
                <div className="space-y-3 text-sm text-gray-700">
                  <p>• All deliveries are fully insured up to R10,000</p>
                  <p>• Tamper-evident packaging ensures document security</p>
                  <p>• GPS tracking available for all delivery options</p>
                  <p>• Signature confirmation required for all deliveries</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Collection Locations</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Johannesburg Office</p>
                      <p className="text-sm text-gray-600">123 Main Street, Johannesburg</p>
                      <p className="text-sm text-gray-600">Mon-Fri: 8AM-6PM, Sat: 9AM-2PM</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Sandton Branch</p>
                      <p className="text-sm text-gray-600">456 Sandton Drive, Sandton</p>
                      <p className="text-sm text-gray-600">Mon-Fri: 8AM-5PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
       <DownloadApp/>
    </>
  );
};

export default Delivery;